import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Shared planner chrome; financial state and API operations stay with each planner.
export const generateDatedPlanName = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = now.toLocaleString('en-US', { month: 'long' });
  return `${day} ${month} ${now.getFullYear()}`;
};
export const PlannerHeader = ({ title, subtitle, onManagePlans }) => (
  <div className="mb-3 flex items-start justify-between gap-3">
    <div className="min-w-0">
      <h1 className="text-xl sm:text-2xl font-bold leading-tight text-gray-900 dark:text-gray-100">{title}</h1>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{subtitle}</div>}
    </div>
    <button onClick={onManagePlans} className="inline-flex shrink-0 items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none">
      <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
      <span className="hidden sm:inline">Manage Plans</span><span className="sm:hidden ml-1">Plans</span>
    </button>
  </div>
);

export const PlannerSurface = ({ children }) => <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-4 mb-6">{children}</div>;

export const PlannerModal = ({ title, onClose, children, busy = false }) => {
  const dialogRef = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dialog = dialogRef.current;
    (dialog.querySelector('[autofocus], input:not(:disabled), button:not(:disabled)') || dialog).focus();
    return () => { document.body.style.overflow = previousOverflow; previousFocus?.focus?.(); };
  }, []);
  const onKeyDown = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); if (!busy) onClose(); }
    if (event.key !== 'Tab') return;
    const elements = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')].filter((element) => element.getClientRects().length);
    const first = elements[0]; const last = elements[elements.length - 1];
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center p-4" onKeyDown={onKeyDown}>
    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => { if (!busy) onClose(); }} />
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button type="button" disabled={busy} onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Close"><svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 0l-4.293-4.293-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
      </div>
      <div className="px-6 py-4 flex-1 min-h-0 overflow-y-auto">{children}</div>
    </div>
  </div>, document.body);
};
